import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useGetResourceBySlugQuery } from "../api/businessApi";
import { ArrowLeft, Calendar, User, Clock, Share2 } from "lucide-react";
import ResourceForm from "../components/ResourceForm";
import SEO from "../components/SEO";

const CONTENT_TYPE_LABELS = {
  newsletter: "Newsletter",
  blog: "Blog",
  video: "Video",
  guide: "Guide",
  announcement: "Announcement",
};

// Convert any YouTube/Vimeo URL to embeddable format
function toEmbedUrl(url) {
  if (!url) return null;
  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID
  let match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (match) return `https://www.youtube-nocookie.com/embed/${match[1]}?rel=0&modestbranding=1`;
  // Vimeo: vimeo.com/ID, player.vimeo.com/video/ID
  match = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
  if (match) return `https://player.vimeo.com/video/${match[1]}`;
  // Already an embed or unknown — return as-is
  return url;
}

const ResourcePage = () => {
  const { slug } = useParams();
  const { data: resource, isLoading, error } = useGetResourceBySlugQuery(slug);
  const [showShareMenu, setShowShareMenu] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#135879] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-2">
            Resource Not Found
          </h2>
          <p className="text-gray-500 mb-4">
            The resource you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/resources"
            className="inline-flex items-center text-[#135879] hover:underline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const handleShare = (platform) => {
    const text = encodeURIComponent(resource.title);
    const url = encodeURIComponent(shareUrl);
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${text}&body=${url}`,
    };
    window.open(urls[platform], "_blank");
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={resource.title}
        description={resource.excerpt || resource.content}
        image={resource.featured_image}
        type="article"
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-[#135879] to-[#0e4562] text-white py-3">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-2">
            <Link
              to="/resources"
              className="inline-flex items-center text-white/70 hover:text-white text-xs transition"
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Back to Resources
            </Link>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#d4af37] text-black">
                {CONTENT_TYPE_LABELS[resource.content_type] ||
                  resource.content_type}
              </span>
              {resource.category && (
                <span className="text-xs text-white/60">
                  {resource.category}
                </span>
              )}
            </div>
          </div>

          <h1 className="text-lg md:text-xl font-bold mb-1">
            {resource.title}
          </h1>

          <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
            {resource.author && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {resource.author}
              </span>
            )}
            {resource.publish_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(resource.publish_date).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
            {resource.read_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {resource.read_time}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Featured Image */}
        {resource.featured_image && (
          <div className="mb-6 max-w-xl mx-auto rounded-xl overflow-hidden shadow-md">
            <img
              src={resource.featured_image}
              alt={resource.title}
              className="w-full h-auto"
            />
          </div>
        )}

        {/* Video Embed */}
        {resource.video_url && (
          <div className="mb-8">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <div className="relative pb-[56.25%] h-0">
                <iframe
                  src={toEmbedUrl(resource.video_url)}
                  title={resource.title}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  referrerPolicy="origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            <div className="mt-3 text-center">
              <a
                href={resource.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#135879] hover:text-[#0e4562] font-medium"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Watch on YouTube
              </a>
            </div>
          </div>
        )}

        {/* HTML Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div
            className="prose prose-lg max-w-none p-6 md:p-8"
            dangerouslySetInnerHTML={{ __html: resource.content }}
          />
        </div>

        {/* Tags */}
        {resource.tags && resource.tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {resource.tags.map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Response Form */}
        <ResourceForm resource={resource} />

        {/* Share & Actions */}
        <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6">
          <Link
            to="/resources"
            className="inline-flex items-center text-[#135879] hover:underline font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Resources
          </Link>

          <div className="relative">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            {showShareMenu && (
              <div className="absolute right-0 bottom-full mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px] z-10">
                {[
                  ["twitter", "Twitter / X"],
                  ["facebook", "Facebook"],
                  ["linkedin", "LinkedIn"],
                  ["email", "Email"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => handleShare(key)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePage;
